import React, { useEffect, useRef, useState } from 'react';
import { useTest } from '../contexts/TestContext';
import SoundUtils from '../utils/SoundUtils';
import { gsap } from 'gsap';

const QuestionPage = () => {
  const { currentQuestion, questions, nextQuestion, prevQuestion, setAnswer, answers, setStage } = useTest();
  const question = questions[currentQuestion];
  const cardRef = useRef(null);
  const containerRef = useRef(null);
  const progressBarRef = useRef(null);
  const optionsRef = useRef([]);
  const redlinesRef = useRef(null);
  const templeRef = useRef(null);
  const overlayRef = useRef(null);
  const questionPageRef = useRef(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const initialRenderRef = useRef(true);
  
  // 重置選項參考陣列
  optionsRef.current = [];
  
  // 計算進度百分比
  const progressPercentage = ((currentQuestion + 1) / questions.length) * 100;
  
  // 處理選項選擇
  const handleOptionSelect = (optionType, optionIndex, e) => {
    // 如果正在動畫中，不處理點擊
    if (isAnimating) return;
    
    // 播放選項音效
    SoundUtils.playOptionSelect();
    
    // 創建波紋效果
    if (optionsRef.current[optionIndex]) {
      const option = optionsRef.current[optionIndex];
      const rect = option.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // 創建波紋元素
      const ripple = document.createElement('span');
      ripple.className = 'ripple';
      ripple.style.left = x + 'px';
      ripple.style.top = y + 'px';
      option.appendChild(ripple);
      
      // 動畫結束後移除波紋元素
      setTimeout(() => {
        ripple.remove();
      }, 600);
    }
    
    // 記錄選擇並更新答案
    setSelectedOption(optionIndex);
    setAnswer(question.id, optionType);
    
    // 應用樣式變化
    // 重置所有選項樣式
    optionsRef.current.forEach((option) => {
      if (option) {
        gsap.set(option, {
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
          border: "2px solid transparent",
          opacity: 1
        });
      }
    });
    
    // 設置選中選項樣式
    if (optionsRef.current[optionIndex]) {
      gsap.set(optionsRef.current[optionIndex], {
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
        border: "2px solid var(--primary-red)"
      });
    }
    
    // 設置未選中選項樣式
    optionsRef.current.forEach((option, index) => {
      if (index !== optionIndex && option) {
        gsap.set(option, {
          opacity: 0.7,
          boxShadow: "0 3px 10px rgba(0,0,0,0.1)"
        });
      }
    });
    
    // 延遲後自動跳轉到下一題
    setTimeout(() => {
      if (currentQuestion === questions.length - 1) {
        transitionToLoadingPage(); // 修改：直接跳轉到Loading頁面
      } else {
        transitionToNextQuestion();
      }
    }, 800);
  };
  
  // 處理上一題按鈕
  const handlePrevQuestion = () => {
    if (currentQuestion === 0 || isAnimating) return;
    
    setIsAnimating(true);
    SoundUtils.playButtonClick();
    
    gsap.to(cardRef.current, {
      y: 30,
      opacity: 0,
      duration: 0.2,
      ease: "power2.in",
      onComplete: () => {
        prevQuestion();
        setIsAnimating(false);
      }
    });
  };
  
  // 修改：直接轉到載入頁面（預告片頁面）
  const transitionToLoadingPage = () => {
    setIsAnimating(true);
    
    gsap.fromTo(overlayRef.current,
      { opacity: 0 },
      { 
        opacity: 0.5, 
        duration: 0.4,
        ease: "power2.inOut",
        onComplete: () => {
          gsap.to(containerRef.current, {
            y: -30,
            opacity: 0,
            duration: 0.2,
            ease: "power2.in",
            onComplete: () => {
              setStage('loading');
            }
          });
        }
      }
    );
  };
  
  // 轉到下一題
  const transitionToNextQuestion = () => {
    setIsAnimating(true);
    
    gsap.to(cardRef.current, {
      y: -30,
      opacity: 0,
      duration: 0.2,
      ease: "power2.in",
      onComplete: () => {
        nextQuestion();
        setSelectedOption(null);
        setIsAnimating(false);
      }
    });
  };
  
  // 頁面初始化效果
  useEffect(() => {
    if (initialRenderRef.current) {
      // 背景元素淡入
      gsap.fromTo(
        questionPageRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.5, ease: "power2.inOut" }
      );
      
      // 容器淡入
      gsap.fromTo(
        containerRef.current, 
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.5, ease: "power2.out", delay: 0.2 }
      );
      
      // 紅線動畫
      gsap.to(redlinesRef.current, {
        y: 15,
        rotation: 3,
        duration: 5,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true
      });
      
      initialRenderRef.current = false;
    }
  }, []);
  
  // 問題變更時的動畫
  useEffect(() => {
    if (cardRef.current) {
      setIsAnimating(true);
      
      // 更新進度條
      gsap.to(progressBarRef.current, {
        width: `${progressPercentage}%`,
        duration: 0.3,
        ease: "power2.inOut"
      });
      
      // 卡片進入動畫
      gsap.fromTo(cardRef.current, 
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.3, ease: "power2.out" }
      );
      
      // 初始化所有選項樣式
      optionsRef.current.forEach((option) => {
        if (option) {
          gsap.set(option, {
            opacity: 0,
            y: 20,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
            border: "2px solid transparent"
          });
        }
      });
      
      // 選項逐個淡入
      optionsRef.current.forEach((option, index) => {
        if (option) {
          gsap.to(option, {
            opacity: 1,
            y: 0,
            duration: 0.2,
            delay: 0.05 + (index * 0.03),
            ease: "power2.out"
          });
        }
      });
      
      // 動畫完成後處理
      setTimeout(() => {
        setIsAnimating(false);
        
        // 檢查是否有已選答案並應用樣式
        if (answers[question.id]) {
          const selectedIndex = question.options.findIndex(
            opt => opt.type === answers[question.id]
          );
          
          if (selectedIndex !== -1) {
            setSelectedOption(selectedIndex);
            
            // 直接設置樣式，不使用動畫
            optionsRef.current.forEach((option, index) => {
              if (option) {
                if (index === selectedIndex) {
                  gsap.set(option, {
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
                    border: "2px solid var(--primary-red)"
                  });
                } else {
                  gsap.set(option, {
                    opacity: 0.7,
                    boxShadow: "0 3px 10px rgba(0,0,0,0.1)"
                  });
                }
              }
            });
          }
        }
      }, 300);
    }
  }, [currentQuestion, answers, question.id, progressPercentage]);
  
  return (
    <div className="question-page" ref={questionPageRef}>
      <div className="temple-background" ref={templeRef}></div>
      <div className="red-lines" ref={redlinesRef}></div>
      <div className="overlay" ref={overlayRef}></div>
      
      <div className="question-container" ref={containerRef}>
        <div className="progress-container">
          <div className="progress-bar-background">
            <div className="progress-bar" ref={progressBarRef} style={{ width: `${progressPercentage}%` }}></div>
          </div>
        </div>
        
        <div className="question-card" ref={cardRef}>
          <h2 className="question-text">{question.text}</h2>
          
          <div className="options-container">
            {question.options.map((option, index) => (
              <div 
                key={option.id}
                className={`option ${selectedOption === index ? 'selected' : ''}`}
                ref={el => optionsRef.current[index] = el}
                onClick={(e) => handleOptionSelect(option.type, index, e)}
              >
                <div className="option-letter">{option.id}</div>
                <div className="option-text">{option.text}</div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="navigation-buttons">
          {currentQuestion > 0 && (
            <button 
              className="nav-button prev-button" 
              onClick={handlePrevQuestion}
              disabled={isAnimating}
            >
              上一題
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionPage;